import { useEffect, useContext, useMemo, useCallback } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import getValidationMessage from 'helpers/getValidationMessage';

import { getMandatoryFileValidations } from 'views/shared/ReferToUnderwriting/helpers';

import * as homeForm from 'reducers/homePersonalFormReducer';

import { FILE_TYPE } from 'variables/constants';

import { getFieldId } from '../resolverUtils';

const VALIDATION_ID = '561';
const FORM_STATE_KEY = 'homePersonalForm' as const;

export type AttestationAnswer = 'yes' | 'no';

export interface UseMultiLineRentalAgreementResolverConfig {
  section: number;
  itemKey: string;
}

export const useMultiLineRentalAgreementResolver = (
  config: UseMultiLineRentalAgreementResolverConfig,
) => {
  const { section, itemKey } = config;

  const { validations } = useContext(ApplicationFormContext);
  const dispatch = useAppDispatch();

  const mandatoryFileValidations = getMandatoryFileValidations(FORM_STATE_KEY);

  const alertMessage = getValidationMessage(validations, VALIDATION_ID);
  const show = !!alertMessage;

  const remark = useAppSelector((state: any) =>
    state[FORM_STATE_KEY].remarks.find(
      (r: any) =>
        r.validation_key === VALIDATION_ID && r.item_key === itemKey,
    ),
  );

  const file = useAppSelector((state: any) =>
    state[FORM_STATE_KEY].files.find(
      (f: any) =>
        f.validation_key === VALIDATION_ID && f.item_key === itemKey,
    ),
  );

  const attestationAnswer: AttestationAnswer | null =
    remark?.custom_data?.attestation_answer ?? null;

  const fieldIds = useMemo(
    () => ({
      attestation: getFieldId(
        'Multi-Line Rental Agreement Attestation',
        itemKey,
      ),
      file: getFieldId('Multi-Line Rental Agreement File', itemKey),
    }),
    [itemKey],
  );

  // Initialize remark when validation becomes visible
  useEffect(() => {
    if (show) {
      dispatch(
        homeForm.addRemark({
          validation_key: VALIDATION_ID,
          item_key: itemKey,
          text: '',
          status: 'created',
          text_validator_log_id: null,
          ai_result: '',
          section,
          custom_data: {
            type: 'multi_line_rental',
            attestation_answer: null,
          },
        }),
      );
    } else {
      dispatch(homeForm.removeRemark({ validation_key: VALIDATION_ID, item_key: itemKey }));
      dispatch(homeForm.removeFile({ validation_key: VALIDATION_ID, item_key: itemKey }));
    }
  }, [show, itemKey, section, dispatch]);

  // Add / remove the file requirement based on attestation answer
  useEffect(() => {
    if (show && attestationAnswer === 'no') {
      dispatch(
        homeForm.addFile({
          validation_key: VALIDATION_ID,
          item_key: itemKey,
          status: 'created',
          file_validator_log_id: null,
          ai_result: '',
          section,
          type: FILE_TYPE.TYPE_561.type,
          files: [],
          text: FILE_TYPE.TYPE_561.text,
          mandatory: mandatoryFileValidations.includes(VALIDATION_ID),
        }),
      );
    } else {
      dispatch(homeForm.removeFile({ validation_key: VALIDATION_ID, item_key: itemKey }));
    }
  }, [show, attestationAnswer, itemKey, section, mandatoryFileValidations, dispatch]);

  // Mandatory field tracking
  useEffect(() => {
    if (!show || !remark) {
      dispatch(homeForm.removeMissingMandatoryField(fieldIds.attestation));
      dispatch(homeForm.removeMissingMandatoryField(fieldIds.file));
      return;
    }

    if (!attestationAnswer) {
      dispatch(
        homeForm.addMissingMandatoryField({ name: fieldIds.attestation, section }),
      );
    } else {
      dispatch(homeForm.removeMissingMandatoryField(fieldIds.attestation));
    }

    if (
      attestationAnswer === 'no' &&
      (!file || (file.status !== 'approved' && !file.is_deferred))
    ) {
      dispatch(homeForm.addMissingMandatoryField({ name: fieldIds.file, section }));
    } else {
      dispatch(homeForm.removeMissingMandatoryField(fieldIds.file));
    }
  }, [show, remark, attestationAnswer, file, section, fieldIds, dispatch]);

  const handleAttestationChange = useCallback(
    (answer: AttestationAnswer) => {
      dispatch(
        homeForm.updateRemark({
          validation_key: VALIDATION_ID,
          item_key: itemKey,
          data: {
            text: '',
            status: 'approved',
            custom_data: {
              type: 'multi_line_rental',
              attestation_answer: answer,
            },
          },
        }),
      );
    },
    [dispatch, itemKey],
  );

  const getCompactAlertStatus = useCallback(() => {
    if (!remark || !attestationAnswer) return 'warning' as const;
    if (
      attestationAnswer === 'no' &&
      (!file || (file.status !== 'approved' && !file.is_deferred))
    )
      return 'warning' as const;
    return 'success' as const;
  }, [remark, attestationAnswer, file]);

  return {
    state: { remark, file, attestationAnswer, show },
    handlers: { handleAttestationChange },
    fieldIds,
    alertMessage,
    getCompactAlertStatus,
  };
};
